// client/src/components/secure/ReferralFees.js
import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';

const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const emptyForm = { direction: 'Outgoing', referring_party_name: '', referring_party_license: '', fee_percentage: '', fee_amount: '', date_agreed: '', notes: '' };

const ReferralFees = ({ transactionId }) => {
    const [fees, setFees] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [formData, setFormData] = useState(emptyForm);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const apiClient = useApiClient();

    const fetchFees = useCallback(() => {
        setIsLoading(true);
        apiClient.get(`/api/referrals?transaction_id=${transactionId}`)
            .then(setFees)
            .catch(err => setError(err.message || 'Failed to load referral fees.'))
            .finally(() => setIsLoading(false));
    }, [transactionId, apiClient]);

    useEffect(() => {
        fetchFees();
    }, [fetchFees]);

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');
        try {
            await apiClient.post('/api/referrals', { ...formData, transaction_id: transactionId });
            setIsFormOpen(false);
            setFormData(emptyForm);
            fetchFees();
        } catch (err) {
            setError(err.message || 'Failed to log referral fee.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const markSettled = async (fee) => {
        try {
            await apiClient.put(`/api/referrals/${fee.referral_fee_id}`, {
                ...fee,
                status: 'Settled',
                date_settled: new Date().toISOString().slice(0, 10),
            });
            fetchFees();
        } catch (err) {
            setError(err.message || 'Failed to update referral fee.');
        }
    };

    return (
        <div className="bg-white p-8 rounded-lg shadow mt-8">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-slate-800">Referral Fees</h2>
                <button onClick={() => setIsFormOpen(true)} className="px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900">
                    Log Referral Fee
                </button>
            </div>

            {isFormOpen && (
                <form onSubmit={handleSubmit} className="bg-slate-50 p-4 rounded-lg mb-6 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <select name="direction" value={formData.direction} onChange={handleChange} className="p-2 border rounded">
                            <option value="Outgoing">Outgoing (we owe them)</option>
                            <option value="Incoming">Incoming (they owe us)</option>
                        </select>
                        <input name="referring_party_name" value={formData.referring_party_name} onChange={handleChange} placeholder="Referring brokerage/agent name" required className="p-2 border rounded" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <input name="referring_party_license" value={formData.referring_party_license} onChange={handleChange} placeholder="Their license # (optional)" className="p-2 border rounded" />
                        <input type="number" step="0.01" name="fee_percentage" value={formData.fee_percentage} onChange={handleChange} placeholder="Fee % (of commission)" className="p-2 border rounded" />
                        <input type="number" step="0.01" name="fee_amount" value={formData.fee_amount} onChange={handleChange} placeholder="Or flat fee $" className="p-2 border rounded" />
                    </div>
                    <input type="date" name="date_agreed" value={formData.date_agreed} onChange={handleChange} className="p-2 border rounded w-full" />
                    <textarea name="notes" value={formData.notes} onChange={handleChange} placeholder="Notes (optional)" rows="2" className="p-2 border rounded w-full"></textarea>
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <div className="flex justify-end gap-3">
                        <button type="button" onClick={() => setIsFormOpen(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                        <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-400">
                            {isSubmitting ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            )}

            {isLoading && <p className="text-slate-500">Loading referral fees...</p>}
            {!isLoading && error && !isFormOpen && <p className="text-red-500">{error}</p>}
            {!isLoading && (
                <ul className="divide-y divide-gray-200">
                    {fees.map(fee => (
                        <li key={fee.referral_fee_id} className="py-3 flex justify-between items-start">
                            <div>
                                <span className="font-semibold text-slate-800">{fee.direction}</span>
                                <span className="text-slate-600"> — {fee.referring_party_name}</span>
                                {fee.referring_party_license && <span className="text-slate-400 text-sm"> (Lic. {fee.referring_party_license})</span>}
                                <div className="text-sm text-slate-500">
                                    {fee.fee_percentage ? `${fee.fee_percentage}% of commission` : formatCurrency(fee.fee_amount)}
                                    {fee.date_agreed && ` — agreed ${new Date(fee.date_agreed).toLocaleDateString()}`}
                                </div>
                                {fee.notes && <p className="text-xs text-slate-500 italic mt-1">{fee.notes}</p>}
                            </div>
                            <div className="text-right">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${fee.status === 'Settled' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{fee.status}</span>
                                {fee.status === 'Pending' && (
                                    <div><button onClick={() => markSettled(fee)} className="text-sm text-indigo-600 hover:underline mt-1">Mark Settled</button></div>
                                )}
                            </div>
                        </li>
                    ))}
                    {fees.length === 0 && <p className="text-slate-500 py-4">No referral fees logged for this transaction.</p>}
                </ul>
            )}
        </div>
    );
};

export default ReferralFees;
